import React, { useEffect, useState } from 'react';
import { Icon, Label } from 'semantic-ui-react';
import useTheme from '../../hooks/useTheme';
import AppText from '../UI/Texts/AppTexts';
import { AppColumn, AppRow } from './Layouts';
import { SizedBox } from './Sizes';
import { useResponsive } from './ResponsiveContext';
import { VerticalDivider } from './CustomElements';
import { t } from 'i18next';

const Snackbar = ({
  title,
  message,
  type = 'info',
  duration = 1600,
  onClose,
  position = 'top-right',
  onCompleted,
}) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true); // Trigger exit animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  useEffect(() => {
    if (isExiting) {
      const exitTimer = setTimeout(() => {
        onClose();
        if (onCompleted) {
          onCompleted(); // Call onCompleted only after animation completes
        }
      }, 200); // Matches animation duration
      return () => clearTimeout(exitTimer);
    }
  }, [isExiting, onClose, onCompleted]);

  const backgroudColor = {
    info: '#e0e0e0',
    success: '#CDFFD6',
    warning: '#FFF8C1',
    error: '#FFD7D7',
  };

  const iconColor = {
    info: '#000000',
    success: theme.colors.green,
    warning: theme.colors.orange,
    error: '#FF0000',
  };

  const iconName = {
    info: 'info circle',
    success: 'check circle',
    warning: 'warning circle',
    error: 'times circle',
  };

  const defaultTitle = {
    info: t('info'),
    success: t('success'),
    warning: t('warning'),
    error: t('error'),
  };

  const positionStyles = {
    'bottom-right': { bottom: theme.spacing.elementPadding, right: theme.spacing.elementPadding },
    'bottom-left': { bottom: theme.spacing.elementPadding, left: theme.spacing.elementPadding },
    'top-right': { top: theme.spacing.contentElementPadding, right: theme.spacing.contentElementPadding },
    'top-left': { top: theme.spacing.contentElementPadding, left: theme.spacing.contentElementPadding },
    'bottom-center': { bottom: theme.spacing.contentPadding, left: 20, right: 20 },
  };

  const effectivePosition = isMobile ? 'bottom-center' : position;


  const toastTitle = title ?? defaultTitle[type]

  return (
    <div
      className={`snackbar ${isExiting ? 'snackbar-exit' : ''}`}
      style={{
        position: 'fixed',
        zIndex: 90000,
        ...positionStyles[effectivePosition],
      }}
    >
      <Label
        size="large"
        color="white"
        style={{
          padding: '1rem',
          width: isMobile ? '100%' : undefined,
          backgroundColor: backgroudColor[type],
          borderRadius: theme.radius.default,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        }}
      >
        <AppRow crossAxisAlignment="center" mainAxisAlignment="space-between">
          <AppRow>
            <Icon name={iconName[type]} size="large" style={{
              color: iconColor[type],
              marginTop: '0.3rem',

            }} />
            <SizedBox width={theme.spacing.elementPaddingSmall} />
            <AppColumn>
              <AppText.Caption
                color={iconColor[type]}
                margins={{ marginBottom: '0px' }}
                fontWeight={600}
              >
                {toastTitle}
              </AppText.Caption>
              <SizedBox height="0.25rem" />
              <AppText.FootnoteSmall color={theme.colors.dark}>
                {message}
              </AppText.FootnoteSmall>
            </AppColumn>
          </AppRow>

          <AppRow>
            <SizedBox width={theme.spacing.contentPadding} />
            <VerticalDivider height="2rem" color={iconColor[type]} />
            <SizedBox width={theme.spacing.elementPaddingSmall} />
            <Icon
              name="close"
              onClick={() => setIsExiting(true)}
              style={{
                cursor: 'pointer',
                color: theme.colors.dark,
              }}
            />
          </AppRow>
        </AppRow>
      </Label>
    </div>
  );
};

export default Snackbar;